import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Check, Crown, Zap, Mail, Bell, BellOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionPage() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/email/preferences`, { withCredentials: true })
      .then(r => setPrefs(r.data))
      .catch(() => setPrefs({
        daily_digest: true,
        weekly_report: true,
        monthly_report: true,
        promotions: true,
        product_updates: true,
      }));
  }, []);

  const togglePref = async (key) => {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/email/preferences`, { [key]: updated[key] }, { withCredentials: true });
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">Your Plan</h1>
        <p className="text-white/55">You are part of the SendPI pilot program</p>
      </div>

      {/* Current Plan */}
      <div className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-white/5 rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-4">
          <Crown className="w-6 h-6 text-amber-400" />
          <h2 className="text-white font-medium text-xl">PILOT PLAN (Free)</h2>
          <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">Active</span>
        </div>
        <p className="text-white/60 mb-6">As one of our first 500 pilot users, you have full access to SendPI at no cost. Your feedback is your payment.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {['10 rewrites per day', 'Full emotion detection', 'Grammar check', 'PI Craft (5 free, then credits)', '200 credits/month', 'Email support', 'Chrome Extension', 'PI Store access'].map(f => (
            <div key={f} className="flex items-center gap-2 text-white/70 text-sm">
              <Check className="w-4 h-4 text-emerald-400 shrink-0" />
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* Credits */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-amber-400" /> Credits</h3>
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-3xl font-bold text-white font-space">{user?.credits_remaining || 0}</span>
          <span className="text-white/40">of {user?.credits_total || 200} credits remaining</span>
        </div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-4">
          <div className="h-full bg-gradient-to-r from-cyan-500 to-violet-500 rounded-full" style={{ width: `${Math.min(100, ((user?.credits_remaining || 0) / (user?.credits_total || 200)) * 100)}%` }} />
        </div>
        <p className="text-white/40 text-sm">Credits reset monthly. During the pilot, all features are available at no cost.</p>
      </div>

      {/* Email Preferences */}
      {prefs && (
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6" data-testid="email-preferences">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-cyan-400" /> Email Preferences
          </h3>
          <div className="space-y-4">
            {[
              { key: 'daily_digest', label: 'Daily Digest', desc: 'Daily summary of your SendPI activity' },
              { key: 'weekly_report', label: 'Weekly PI Insights', desc: 'Weekly communication intelligence report' },
              { key: 'monthly_report', label: 'Monthly Report', desc: 'Comprehensive monthly performance summary' },
              { key: 'promotions', label: 'Promotions', desc: 'Special offers and new features' },
              { key: 'product_updates', label: 'Product Updates', desc: 'Platform updates and announcements' },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between py-2">
                <div>
                  <div className="text-white text-sm font-medium">{item.label}</div>
                  <div className="text-white/40 text-xs">{item.desc}</div>
                </div>
                <Switch
                  checked={prefs[item.key]}
                  onCheckedChange={() => togglePref(item.key)}
                  data-testid={`email-pref-${item.key}`}
                />
              </div>
            ))}
          </div>
          {saving && <p className="text-xs text-white/30 mt-2">Saving...</p>}
        </div>
      )}

      {/* Future Plans */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
        <h3 className="text-white font-medium mb-3">Paid Plans</h3>
        <p className="text-white/50 text-sm mb-4">Paid plans will be available after the pilot concludes. As a pilot user, you'll get early access and exclusive pricing.</p>
        <div className="grid grid-cols-3 gap-3">
          {['Starter', 'Pro', 'Team'].map(p => (
            <div key={p} className="bg-white/5 rounded-xl p-4 text-center">
              <span className="text-white/60 text-sm font-medium">{p}</span>
              <span className="block text-xs text-white/30 mt-1">Coming Soon</span>
            </div>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6 flex items-center gap-4">
        <Mail className="w-6 h-6 text-cyan-400 shrink-0" />
        <div>
          <h3 className="text-white font-medium">Questions about your plan?</h3>
          <p className="text-white/50 text-sm">Email <a href="mailto:support@sendpi.ai" className="text-cyan-400 hover:underline">support@sendpi.ai</a> anytime</p>
        </div>
      </div>
    </div>
  );
}
